import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PlatformService } from 'app/core/platform/platform.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { AnalyticsService } from 'app/modules/user/analytics/analytics.service';
import { AnalyticsFilter, AnalyticsFilterConfig, AnalyticsFilterType, NewFilterConfig } from 'app/modules/user/analytics/analytics.types';
import { Artist, Label, Release, Store, Track } from 'app/modules/user/releases/releases.types';
import { Country } from 'app/shared/shared.types';
import { UtilService } from 'app/shared/util.service';
import { cloneDeep } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-filter-bar',
    templateUrl: './filter-bar.component.html',
    styleUrls: ['./filter-bar.component.scss']
})
export class FilterBarComponent implements OnInit, OnDestroy {
    @Input() preSelectedRelease: Release;
    @Input() newFilterAdded: Observable<NewFilterConfig>;
    @Input() filterBlacklist: string[];
    @Input() filterMode: 'trends' | 'sales' | 'artificials' = 'trends';
    @Input() showNoiseFilter = false;
    @Output() onFilterChanged = new EventEmitter<AnalyticsFilterConfig>();

    currentFilter: AnalyticsFilterConfig;
    countries: Country[];
    filteredCountries: Country[];
    artistFilters: Artist[];
    stores: Store[];
    releaseFilters: Release[];
    trackFilters: Track[];
    labelFilters: Label[];
    userFilters: User[];
    noiseFilter?: boolean = null!;
    user: User;
    searchForm: FormGroup;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        public analyticsService: AnalyticsService,
        public utilService: UtilService,
        public platformService: PlatformService,
        private _userService: UserService,
        private _formBuilder: FormBuilder,
        private _utilService: UtilService,
    ) {
    }

    ngOnInit(): void {
        this._utilService.countries$.pipe(takeUntil(this._unsubscribeAll)).subscribe(c => {
            this.countries = c;
        });
        this.filteredCountries = this.countries;
        this._userService.user$.pipe(take(1)).subscribe(u => this.user = u);

        this.currentFilter = new AnalyticsFilterConfig();

        if (this.newFilterAdded) {
            this.newFilterAdded.pipe(takeUntil(this._unsubscribeAll)).subscribe((newFilter) => {
                if (!newFilter) {
                    return;
                }
                this.addFilter(newFilter.type, newFilter.value);
            });
        }

        if (this.preSelectedRelease) {
            this.addFilter('releases', { uuid: this.preSelectedRelease.uuid, title: this.preSelectedRelease.title });
        }

        this.searchForm = this._formBuilder.group({
            users: [''],
            artists: [''],
            releases: [''],
            tracks: [''],
            labels: [''],
            countries: [''],
        });

        Object.entries(this.searchForm.controls).forEach(control => {
            control[1].valueChanges.pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(300)
            ).subscribe((value) => {
                if (value === undefined || value === null) {
                    return;
                }
                this.loadFilterData(control[0] as AnalyticsFilterType, value);
            });
        })
    }

    ngOnDestroy() {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    addFilter(filterType: AnalyticsFilterType, value: AnalyticsFilter) {
        this.filteredCountries = this.countries;
        this.currentFilter.add(filterType, value);
        this.updateOutput();
    }

    showFilter(filterType: AnalyticsFilterType) {
        if (this.filterBlacklist) {
            return !this.filterBlacklist.includes(filterType);
        }

        return true;
    }

    getFullUserName(user: User) {
        if (user.personalInformation && user.personalInformation.firstName && user.personalInformation.lastName) {
            return user.personalInformation.firstName + " " + user.personalInformation.lastName;
        }

        return "???";
    }

    getArtistName(artistId: string): string {
        return this.artistFilters?.find(artist => artist.uuid === artistId)?.title || '???';
    }

    getStoreName(storeId: string): string {
        return this.stores?.find(store => store.uuid === storeId)?.title || '???';
    }

    getUserName(userId: string) {
        let user = this.userFilters?.find(user => user.uuid === userId);
        return user ? `${user.personalInformation.firstName} ${user.personalInformation.lastName}` : '???';
    }

    getReleaseName(releaseId: string): string {
        return this.releaseFilters?.find(release => release.uuid === releaseId)?.title || '???';
    }

    getTrackName(track: string) {
        return this.trackFilters?.find((x) => x.uuid === track).title;
    }

    getTypeName(type: string) {
        return type.charAt(0).toUpperCase() + type.slice(1);
    }

    getCountryName(uuid: string) {
        return this.countries.find(c => c.uuid === uuid)?.name || '???';
    }

    removeFilter(filterType: AnalyticsFilterType, value: AnalyticsFilter) {
        this.currentFilter.remove(filterType, value);
        if (filterType === 'releases') {
            this.currentFilter.tracks = [];
        }
        this.updateOutput();
    }

    updateOutput() {
        this.onFilterChanged.emit(cloneDeep(this.currentFilter));
    }

    loadFilterData(type: AnalyticsFilterType, search?: string) {
        if (!search) {
            this.searchForm.reset();
        }

        switch (type) {
            case 'users':
                this.analyticsService.getUserFilters(this.currentFilter, search).pipe(take(1)).subscribe((users: User[]) => {
                    if (!search) {
                        this.userFilters = users;
                    }
                });
                break;
            case 'artists':
                this.analyticsService.getArtistFilters(this.currentFilter, search).pipe(take(1)).subscribe((artists: Artist[]) => {
                    if (!search) {
                        this.artistFilters = artists;
                    }
                });
                break;
            case 'releases':
                this.analyticsService.getReleaseFilters(this.currentFilter, search).pipe(take(1)).subscribe((result) => {
                    if (!search) {
                        this.releaseFilters = result
                    }
                });
                break;
            case 'tracks':
                this.analyticsService.getTrackFilters(this.currentFilter, search).pipe(take(1)).subscribe((result) => {
                    if (!search) {
                        this.trackFilters = result
                    }
                });
                break;
            case 'labels':
                this.analyticsService.getLabelFilters(this.currentFilter, search).pipe(take(1)).subscribe((result) => {
                    if (!search) {
                        this.labelFilters = result
                    }
                });
                break;
            case 'stores':
                switch (this.filterMode) {
                    case 'trends':
                        this.analyticsService.getStores(this.currentFilter).pipe(take(1)).subscribe((result) => this.stores = result);
                        break;
                    case 'sales':
                        //this.analyticsService.getSalesStores(this.currentFilter).pipe(take(1)).subscribe((result) => this.stores = result);
                        break;
                    case 'artificials':
                        //this.analyticsService.getArtificialsStores(this.currentFilter).pipe(take(1)).subscribe((result) => this.stores = result);
                        this.analyticsService.getStores(this.currentFilter).pipe(take(1)).subscribe((result) => this.stores = result);
                        break;
                }
                break;
            case 'countries':
                this.filteredCountries = this.countries.filter(country => {
                    search = search.toLowerCase();
                    return country.alpha2.toLowerCase() === search || country.name.toLowerCase().startsWith(search);
                })
                break;
            default:
                console.warn('Unimplemented filter type:', type);
        }
    }

    stopPropagation($event: MouseEvent) {
        $event.preventDefault();
        $event.stopPropagation();
    }

    trackByFn(index: number, item: any): any {
        return item.uuid || index;
    }
}
